import { useInterval } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getLastVisits } from '../model/lastVisits';

function collectUserIds(data: unknown, ids: Set<string>): void {
  if (data == null || typeof data !== 'object') return;

  if (Array.isArray(data)) {
    for (const item of data) {
      collectUserIds(item, ids);
    }
    return;
  }

  const obj = data as Record<string, unknown>;
  if (typeof obj.id === 'string' && 'lastVisit' in obj && 'displayName' in obj) {
    ids.add(obj.id);
  }

  for (const value of Object.values(obj)) {
    if (value != null && typeof value === 'object') {
      collectUserIds(value, ids);
    }
  }
}

function applyLastVisits(data: unknown, visitMap: Map<string, string>): unknown {
  if (data == null || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    let changed = false;
    const result = data.map((item) => {
      const updated = applyLastVisits(item, visitMap);
      if (updated !== item) changed = true;
      return updated;
    });
    return changed ? result : data;
  }

  const obj = data as Record<string, unknown>;
  let changed = false;
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (
      key === 'lastVisit' &&
      typeof obj.id === 'string' &&
      'displayName' in obj &&
      visitMap.has(obj.id)
    ) {
      const newVisit = visitMap.get(obj.id)!;
      if (newVisit !== value) {
        result[key] = newVisit;
        changed = true;
        continue;
      }
    }

    if (value != null && typeof value === 'object') {
      const updated = applyLastVisits(value, visitMap);
      if (updated !== value) changed = true;
      result[key] = updated;
    } else {
      result[key] = value;
    }
  }

  return changed ? result : data;
}

/**
 * Держит отметки «в сети / был N минут назад» свежими, пока страница открыта: раз в минуту
 * собирает всех показанных сейчас людей и обновляет им время последнего визита прямо в кэше.
 *
 * Клиент берётся хуком, а не из модульного синглтона: их в приложении два, и синглтон — не тот,
 * из которого читают компоненты. Обход его кэша всегда находил ноль людей и тихо выходил, поэтому
 * зелёные точки замирали в том виде, в каком страница загрузилась.
 */
export const useLastVisits = () => {
  const queryClient = useQueryClient();

  const updateLastVisits = useCallback(async () => {
    // В фоновой вкладке отметки никто не видит — незачем и спрашивать.
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    try {
      const cache = queryClient.getQueryCache();
      const ids = new Set<string>();

      for (const query of cache.getAll()) {
        collectUserIds(query.state.data, ids);
      }

      if (ids.size === 0) return;

      const visits = await getLastVisits([...ids]);
      if (!visits || visits.length === 0) return;

      const visitMap = new Map<string, string>();
      for (const visit of visits) {
        if (visit.userId != null && visit.lastVisit != null) {
          visitMap.set(visit.userId, visit.lastVisit);
        }
      }

      for (const query of cache.getAll()) {
        const data = query.state.data;
        if (data == null) continue;

        const updated = applyLastVisits(data, visitMap);
        if (updated !== data) {
          queryClient.setQueryData(query.queryKey, updated);
        }
      }
    } catch {
      // last visits is best-effort, silently ignore errors
    }
  }, [queryClient]);

  useInterval(updateLastVisits, 60_000, { autoInvoke: true });
};
